import { notificationHref } from "@/lib/task-notification-link";
import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  BellRing,
  Check,
  CheckCheck,
  MessageCircle,
  UserPlus,
  RefreshCcw,
  Zap,
  ExternalLink,
  X,
  Filter,
  Wifi,
  WifiOff,
  Archive,
  Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  useRecentNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useNotificationSubscription,
} from "@/hooks/useNotifications";
import { useDesktopNotifications } from "@/hooks/useDesktopNotifications";
import { toast } from "sonner";
import type { NotificationRecord } from "@/api/notifications";
import { formatDistanceToNow } from "date-fns";
import { Alert, AlertDescription } from "@/components/ui/alert";

// ────────────────────────────────────────────────────────────────────────────
// Constants & types
// ────────────────────────────────────────────────────────────────────────────

type FilterTab = "all" | "mentions" | "assignments" | "comments" | "automations";

const FILTER_TABS: { value: FilterTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: "all", label: "All", icon: Bell },
  { value: "mentions", label: "Mentions", icon: MessageCircle },
  { value: "assignments", label: "Assigned", icon: UserPlus },
  { value: "comments", label: "Comments", icon: MessageCircle },
  { value: "automations", label: "Auto", icon: Zap },
];

const FILTER_TYPE_MAP: Record<FilterTab, string | null> = {
  all: null,
  mentions: "mention",
  assignments: "assignment",
  comments: "comment",
  automations: "automation",
};

// Icon mapping based on notification type
const notificationIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  mention: MessageCircle,
  assignment: UserPlus,
  status_change: RefreshCcw,
  comment: MessageCircle,
  automation: Zap,
};

function getNotificationIcon(type: string | null) {
  if (!type) return Bell;
  return notificationIcons[type] || Bell;
}

// ────────────────────────────────────────────────────────────────────────────
// NotificationItem — rich card with inline actions
// ────────────────────────────────────────────────────────────────────────────

interface NotificationItemProps {
  notification: NotificationRecord;
  onMarkAsRead: (notificationId: string) => void;
  onClose: () => void;
}

function NotificationItem({ notification, onMarkAsRead, onClose }: NotificationItemProps) {
  const Icon = getNotificationIcon(notification.notification_type);
  const actorName = notification.actor
    ? `${notification.actor.first_name ?? ""} ${notification.actor.last_name ?? ""}`.trim()
    : null;

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }
    if (notification.link_url) {
      onClose();
    }
  };

  const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: true,
  });

  return (
    <div
      className={cn(
        "group relative flex gap-3 rounded-lg p-3 transition-colors hover:bg-accent/50",
        !notification.is_read && "bg-primary/5"
      )}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Icon */}
      <div
        className={cn(
          "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          !notification.is_read ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
        )}
      >
        <Icon className="h-4 w-4" />
      </div>

      {/* Content */}
      <div className="flex-1 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium leading-tight">{notification.title}</p>
          <div className="flex shrink-0 items-center gap-1">
            {!notification.is_read && (
              <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
            )}
            {/* Quick-action: mark as read on hover */}
            {!notification.is_read && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkAsRead(notification.id);
                    }}
                    aria-label="Mark as read"
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">Mark as read</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {notification.body && (
          <p className="text-sm text-muted-foreground line-clamp-2">{notification.body}</p>
        )}

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {actorName && <span>{actorName}</span>}
          {actorName && <span>·</span>}
          <time dateTime={notification.created_at}>{timeAgo}</time>
          {notification.notification_type && (
            <>
              <span>·</span>
              <Badge variant="outline" className="h-4 px-1 text-[10px] capitalize">
                {notification.notification_type.replace("_", " ")}
              </Badge>
            </>
          )}
        </div>

        {notification.link_url && (
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="mt-1 h-auto p-0 text-xs text-primary hover:bg-transparent hover:underline"
          >
            <a href={notificationHref(notification)} className="inline-flex items-center gap-1">
              {notification.link_label || "View details"}
              <ExternalLink className="h-3 w-3" />
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// NotificationGroup
// ────────────────────────────────────────────────────────────────────────────

interface NotificationGroupProps {
  title: string;
  notifications: NotificationRecord[];
  onMarkAsRead: (notificationId: string) => void;
  onClose: () => void;
}

function NotificationGroup({ title, notifications, onMarkAsRead, onClose }: NotificationGroupProps) {
  if (notifications.length === 0) return null;

  return (
    <div className="space-y-1">
      <div className="px-3 py-2">
        <h4 className="text-xs font-semibold text-muted-foreground">
          {title}
        </h4>
      </div>
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onMarkAsRead={onMarkAsRead}
          onClose={onClose}
        />
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// NotificationCenter — main popover
// ────────────────────────────────────────────────────────────────────────────

interface NotificationCenterProps {
  /** Notified when the panel opens/closes (telemetry-agnostic; the caller decides what to log). */
  onOpenChange?: (open: boolean) => void;
  /** AppLayout supplies its persistent subscription, so hiding the bell cannot disconnect it. */
  realtimeConnection?: { realtimeConnected: boolean };
}

export function NotificationCenter(props: NotificationCenterProps = {}) {
  return props.realtimeConnection
    ? <NotificationCenterContent {...props} realtimeConnection={props.realtimeConnection} />
    : <SubscribedNotificationCenter {...props} />;
}

/** Standalone callers retain the original designated subscriber behavior. */
function SubscribedNotificationCenter(props: NotificationCenterProps) {
  const realtimeConnection = useNotificationSubscription({ showDesktopNotifications: true });
  return <NotificationCenterContent {...props} realtimeConnection={realtimeConnection} />;
}

function NotificationCenterContent({ onOpenChange, realtimeConnection }: NotificationCenterProps & { realtimeConnection: { realtimeConnected: boolean } }) {
  const [open, setOpen] = useState(false);
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };
  const [showDesktopPrompt, setShowDesktopPrompt] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterTab>("all");
  const { data: unreadCount = 0 } = useUnreadCount();
  const { data: recentNotifications = [] } = useRecentNotifications(20);
  const markAsReadMutation = useMarkAsRead();
  const markAllAsReadMutation = useMarkAllAsRead();
  const { permission, isSupported, requestPermission } = useDesktopNotifications();

  // Subscribe to real-time updates (Broadcast + postgres_changes)
  const { realtimeConnected } = realtimeConnection;

  const handleRequestDesktopPermission = async () => {
    const result = await requestPermission();
    if (result === "granted") {
      toast.success("Desktop notifications enabled");
      setShowDesktopPrompt(false);
    } else if (result === "denied") {
      toast.error("Desktop notifications blocked", {
        description: "Please enable them in your browser settings.",
      });
    }
  };

  // Filter notifications by type
  const filteredNotifications = useMemo(() => {
    const typeFilter = FILTER_TYPE_MAP[activeFilter];
    if (!typeFilter) return recentNotifications;
    return recentNotifications.filter(
      (n) => n.notification_type === typeFilter
    );
  }, [recentNotifications, activeFilter]);

  // Count per filter tab (for badges)
  const filterCounts = useMemo(() => {
    const counts: Record<FilterTab, number> = {
      all: 0,
      mentions: 0,
      assignments: 0,
      comments: 0,
      automations: 0,
    };
    recentNotifications.forEach((n) => {
      if (!n.is_read) {
        counts.all++;
        if (n.notification_type === "mention") counts.mentions++;
        if (n.notification_type === "assignment") counts.assignments++;
        if (n.notification_type === "comment") counts.comments++;
        if (n.notification_type === "automation") counts.automations++;
      }
    });
    return counts;
  }, [recentNotifications]);

  // Group notifications by time
  const groupedNotifications = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const unread: NotificationRecord[] = [];
    const earlierToday: NotificationRecord[] = [];
    const thisWeekList: NotificationRecord[] = [];
    const older: NotificationRecord[] = [];

    filteredNotifications.forEach((notification) => {
      const createdAt = new Date(notification.created_at);

      if (!notification.is_read) {
        unread.push(notification);
      } else if (createdAt >= today) {
        earlierToday.push(notification);
      } else if (createdAt >= thisWeek) {
        thisWeekList.push(notification);
      } else {
        older.push(notification);
      }
    });

    return { unread, earlierToday, thisWeek: thisWeekList, older };
  }, [filteredNotifications]);

  const handleMarkAsRead = useCallback(
    (notificationId: string) => {
      markAsReadMutation.mutate(
        { notificationId, isRead: true },
        {
          onError: (error) => {
            toast.error("Failed to mark notification as read", {
              description: error instanceof Error ? error.message : "Please try again.",
            });
          },
        }
      );
    },
    [markAsReadMutation]
  );

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsReadMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success("All notifications marked as read");
      },
      onError: (error) => {
        toast.error("Failed to mark all as read", {
          description: error instanceof Error ? error.message : "Please try again.",
        });
      },
    });
  }, [markAllAsReadMutation]);

  const hasNotifications = filteredNotifications.length > 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
          title="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[420px] p-0"
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">Notifications</h3>
            {/* Realtime connection indicator */}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center">
                  {realtimeConnected ? (
                    <Wifi className="h-3 w-3 text-green-500" />
                  ) : (
                    <WifiOff className="h-3 w-3 text-muted-foreground" />
                  )}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {realtimeConnected ? "Live updates active" : "Reconnecting…"}
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 text-xs text-muted-foreground hover:bg-transparent hover:text-primary"
                onClick={handleMarkAllAsRead}
                disabled={markAllAsReadMutation.isPending}
              >
                <CheckCheck className="mr-1 h-3 w-3" />
                Mark all read
              </Button>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  asChild
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                >
                  <Link to="/settings/notifications" onClick={() => setOpen(false)}>
                    <Settings className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Notification settings</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-1 border-b px-3 py-2 overflow-x-auto">
          {FILTER_TABS.map((tab) => {
            const count = filterCounts[tab.value];
            const isActive = activeFilter === tab.value;
            return (
              <Button
                key={tab.value}
                variant={isActive ? "secondary" : "ghost"}
                size="sm"
                className={cn(
                  "h-7 gap-1 rounded-full px-3 text-xs",
                  isActive && "font-medium"
                )}
                onClick={() => setActiveFilter(tab.value)}
              >
                {tab.label}
                {count > 0 && (
                  <Badge
                    variant="destructive"
                    className="ml-0.5 h-4 min-w-4 px-1 text-[10px]"
                  >
                    {count}
                  </Badge>
                )}
              </Button>
            );
          })}
        </div>

        {/* Desktop notification prompt */}
        {isSupported && permission === "default" && showDesktopPrompt && (
          <div className="border-b px-3 py-2">
            <Alert className="relative border-primary/20 bg-primary/5">
              <BellRing className="h-4 w-4 text-primary" />
              <AlertDescription className="flex items-start justify-between gap-2 pr-6">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Enable desktop notifications?</p>
                  <p className="text-xs text-muted-foreground">
                    Get notified about mentions, assignments, and important updates.
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="primary" onClick={handleRequestDesktopPermission}>
                    Enable
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowDesktopPrompt(false)}
                    className="h-auto px-2"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Notifications list */}
        <ScrollArea className="h-[400px]">
          {!hasNotifications ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                {activeFilter === "all" ? (
                  <Bell className="h-6 w-6 text-muted-foreground" />
                ) : (
                  <Filter className="h-6 w-6 text-muted-foreground" />
                )}
              </div>
              <p className="text-sm font-medium">
                {activeFilter === "all"
                  ? "You're all caught up"
                  : `No ${activeFilter} notifications`}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {activeFilter === "all"
                  ? "We'll notify you when there's something new"
                  : "Try a different filter or check back later"}
              </p>
            </div>
          ) : (
            <div className="py-2">
              <NotificationGroup
                title="Unread"
                notifications={groupedNotifications.unread}
                onMarkAsRead={handleMarkAsRead}
                onClose={() => setOpen(false)}
              />
              {groupedNotifications.unread.length > 0 &&
                (groupedNotifications.earlierToday.length > 0 ||
                  groupedNotifications.thisWeek.length > 0 ||
                  groupedNotifications.older.length > 0) && (
                  <Separator className="my-2" />
                )}
              <NotificationGroup
                title="Earlier today"
                notifications={groupedNotifications.earlierToday}
                onMarkAsRead={handleMarkAsRead}
                onClose={() => setOpen(false)}
              />
              {groupedNotifications.earlierToday.length > 0 &&
                (groupedNotifications.thisWeek.length > 0 ||
                  groupedNotifications.older.length > 0) && (
                  <Separator className="my-2" />
                )}
              <NotificationGroup
                title="This week"
                notifications={groupedNotifications.thisWeek}
                onMarkAsRead={handleMarkAsRead}
                onClose={() => setOpen(false)}
              />
              {groupedNotifications.thisWeek.length > 0 &&
                groupedNotifications.older.length > 0 && (
                  <Separator className="my-2" />
                )}
              <NotificationGroup
                title="Older"
                notifications={groupedNotifications.older}
                onMarkAsRead={handleMarkAsRead}
                onClose={() => setOpen(false)}
              />
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {hasNotifications && (
          <>
            <Separator />
            <div className="p-2">
              <Button
                asChild
                variant="ghost"
                className="w-full justify-center text-sm"
                onClick={() => setOpen(false)}
              >
                <Link to="/notifications">View all notifications</Link>
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
